import { cn } from '#/shared/lib/utils'

interface CampaignCardProps {
  children: React.ReactNode
  className?: string
}

/**
 * The section card the whole campaign detail is built from: a hairline box with
 * an optional label strip on top. Distinct from the shared `Card`, which is the
 * shadowed shadcn surface with 24px padding — this one is flush chrome.
 */
export function CampaignCard({ children, className }: CampaignCardProps) {
  return (
    <section
      className={cn(
        'overflow-hidden rounded-lg border border-gray-200 bg-white',
        className,
      )}
    >
      {children}
    </section>
  )
}

interface CampaignCardHeaderProps {
  label: string
  /** Right-side affordance — usually a link ("Edit", "See all bounces"). */
  action?: React.ReactNode
  /** Rail cards run 2px tighter than the wide column. */
  dense?: boolean
}

export function CampaignCardHeader({
  label,
  action,
  dense,
}: CampaignCardHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2.5 border-b border-gray-200 py-3',
        dense ? 'px-3.5' : 'px-4',
      )}
    >
      <h2 className="text-2xs font-medium tracking-wider text-gray-300">
        {label}
      </h2>
      {action}
    </div>
  )
}

interface CampaignCardBodyProps {
  children: React.ReactNode
  dense?: boolean
  className?: string
}

export function CampaignCardBody({
  children,
  dense,
  className,
}: CampaignCardBodyProps) {
  return (
    <div
      className={cn(
        'flex flex-col',
        dense ? 'gap-2.5 px-3.5 pt-3 pb-3.5' : 'gap-3 px-4 pt-3.5 pb-4',
        className,
      )}
    >
      {children}
    </div>
  )
}

/** The blue "Edit" / "See all bounces" affordance in a card header. */
export function CampaignCardAction({
  children,
  onClick,
}: {
  children: React.ReactNode
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer text-xs font-medium text-campaign-running hover:underline"
    >
      {children}
    </button>
  )
}
